/* eslint-disable jira/filename-case */
import {
	useMemo,
	useContext,
	useRef,
	useCallback,
	useState,
	useEffect,
	useDebugValue,
} from 'react';
import isShallowEqualOld from '@atlassian/jira-common-util-is-shallow-equal/src/index.tsx';
import { isShallowEqual as isShallowEqualNew } from '@jira/platform__utils-shallow-equal/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { Context } from '../context.tsx';
import type { StoreState, Store, ActionThunk, BoundActions, Selector } from '../types.tsx';
import { getSelectorInstance } from '../utils/create-selector.utils.tsx';
import { useStoreState } from './useStoreState.tsx';

// Remove this and use the new isShallowEqualNew directly when the feature flag is cleaned up.
const isShallowEqual = (a: unknown, b: unknown): boolean => {
	if (fg('rss_use_new_valid_shallow_equal')) {
		return isShallowEqualNew(a, b);
	}
	return isShallowEqualOld(a, b);
};

const EMPTY_SELECTOR = () => undefined;
const DEFAULT_SELECTOR = <TState,>(state: StoreState<TState>) => state;

export type HookReturnValue<TState, TActions> = [TState, TActions];
export type HookFunction<TState, TActions, TArg = void> = (
	...args: TArg extends unknown ? (TArg extends undefined ? [TArg] | [] : [TArg]) : []
) => HookReturnValue<TState, TActions>;

export type HookActionsFunction<TActions> = () => TActions;

export type HookStateFunction<TState, TArg = undefined> = (
	...args: TArg extends undefined ? [] : [TArg]
) => TState;

export function createHook<
	TState,
	TActions extends Record<string, ActionThunk<TState, TActions>>,
	TSelectedState = TState,
	THookArg = void,
>(
	store: Store<TState, TActions>,
	options?: {
		selector?: Selector<TState, THookArg, TSelectedState> | null;
	},
): HookFunction<TSelectedState, BoundActions<TState, TActions>, THookArg>;

// oxlint-disable-next-line no-unused-vars
export function createHook<TState, TActions extends Record<string, ActionThunk<TState, TActions>>>(
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Store: any,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	{ selector }: any = {},
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
): any {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return function useSweetState(propsArg?: any) {
		useDebugValue(`RSS: ${Store.key}`);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const { retrieveStore }: any = useContext(Context);
		const { storeState, actions, behavior } = retrieveStore(Store);

		const hasPropsArg = propsArg !== undefined;
		const propsArgRef = useRef(propsArg);
		propsArgRef.current = propsArg;

		const stateSelector = useMemo(() => {
			if (selector) {
				return getSelectorInstance(selector, storeState, hasPropsArg);
			}

			return selector === null ? EMPTY_SELECTOR : DEFAULT_SELECTOR;
		}, [hasPropsArg, storeState]);

		const forceUpdate = useState({})[1];
		const getSnapshot = useCallback(() => {
			// parent scope has changed and notify was explicitly triggered by the container
			// we need to force the hook to re-render to listen new storeState
			if (retrieveStore(Store).storeState !== storeState) forceUpdate({});

			const state = storeState.getState();
			return stateSelector(state, propsArgRef.current);
		}, [retrieveStore, storeState, stateSelector, forceUpdate]);

		const currentState = useStoreState(storeState.subscribe, getSnapshot, behavior);

		return [currentState, actions];
	};
}

/**
 * Use this hook to select state from store within react components. This removes the need to use
 * selectors manually, which are difficult to create, bad for code splitting,
 * have bad type safety, and can cause unnecessary re-renders.
 *
 * Example:
 * // in store.tsx
 * const useCounterStore = createSelectorHook(counterStore);
 * // in component.tsx
 * const count = useCounterStore(useCallback((state) => state.count, []));
 */
export function createSelectorHook<
	TState,
	TActions extends Record<string, ActionThunk<TState, TActions>>,
>(Store: Store<TState, TActions>) {
	return function useSweetState<U>(selector: (state: TState) => U): U {
		useDebugValue(`RSS: ${Store.key}`);
		const { retrieveStore } = useContext(Context);
		const { storeState, behavior } = retrieveStore(Store);
		const [, forceUpdate] = useState({});
		const previousSelectorRef = useRef(selector);
		const selectorRef = useRef(selector);
		const selectedStateRef = useRef(selectorRef.current(storeState.getState()));
		const previousState = useRef(storeState.getState());

		useEffect(() => {
			if (selectorRef.current !== selector) {
				selectorRef.current = selector;
				if (!isShallowEqual(selectorRef.current(storeState.getState()), selectedStateRef.current)) {
					forceUpdate({});
				}
			}
		}, [selector, storeState]);

		const getSnapshot = useCallback(() => {
			const state = storeState.getState();
			if (previousState.current !== state || previousSelectorRef.current !== selectorRef.current) {
				const nextSelectedState = selectorRef.current(state);

				if (process.env.NODE_ENV !== 'production') {
					const nextSelectedStateAgain = selectorRef.current(state);
					if (!isShallowEqual(nextSelectedStateAgain, nextSelectedState)) {
						throw new Error(
							'⚠️ Your selector function is not stable and returns different results on each call. This will cause infinite re-renders and break the application. To fix this, make sure your selector function allows for shallow referential equality.',
						);
					}
				}

				if (isShallowEqual(nextSelectedState, selectedStateRef.current)) {
					return selectedStateRef.current;
				}

				previousSelectorRef.current = selectorRef.current;
				selectedStateRef.current = nextSelectedState;
				previousState.current = state;
			}

			return selectedStateRef.current;
		}, [storeState]);

		return useStoreState(storeState.subscribe, getSnapshot, behavior);
	};
}

export function createActionsHook<
	TState,
	TActions extends Record<string, ActionThunk<TState, TActions>>,
>(store: Store<TState, TActions>): HookActionsFunction<BoundActions<TState, TActions>>;

export function createActionsHook<
	TState,
	// oxlint-disable-next-line no-unused-vars
	TActions extends Record<string, ActionThunk<TState, TActions>>,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
>(Store: any): any {
	const useHook = createHook(Store, { selector: null });
	return function useSweetStateActions() {
		return useHook()[1];
	};
}

export function createStateHook<
	TState,
	TActions extends Record<string, ActionThunk<TState, TActions>>,
	TSelectedState = TState,
	THookArg = void,
>(
	store: Store<TState, TActions>,
	options?: {
		selector?: Selector<TState, THookArg, TSelectedState>;
	},
): HookStateFunction<TSelectedState, THookArg>;

export function createStateHook<
	TState,
	// oxlint-disable-next-line no-unused-vars
	TActions extends Record<string, ActionThunk<TState, TActions>>,
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
>(Store: any, { selector }: any = {}): any {
	const useHook = createHook(Store, { selector });
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return function useSweetStateState(propsArg: any) {
		return useHook(propsArg)[0];
	};
}
